/**
 * Sprachprinzip Flex4Success v8 (verbindlich):
 * 1. Alltagssprache, keine Anglizismen-Übersetzungen
 * 2. Metaphern aus Andreas' Coaching-Modell (Sanduhr, Radar, Tür/Schlüssel, Standortbestimmung)
 * 3. Psychologisch fundiert, nicht esoterisch
 * 4. Kein Coach-Sprech
 * 5. Werkzeug-Namen in einem Satz erklärbar
 * 6. 'Primärfokustyp' statt 'Fokustyp' im Singular-Kontext
 *
 * Sprint M5.39 (28.07.2026) — Wording-Konsolidierung v8:
 * Pricing-Tier-Namen final gesetzt (Free → Schnupper-Plan, Pro Solo Monat →
 * Monatsplan, Pro Solo Jahr → Jahresplan). Body-Prosa/Bullets bleibt Franks
 * Domäne (Phase 5).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import DashboardHeader from "@/components/DashboardHeader";
import Footer from "@/components/Footer";
import MarketingBanner from "@/components/marketing/MarketingBanner";
import CampaignModal from "@/components/marketing/CampaignModal";
import { Tag, Check, ArrowRight, Sparkles, Package, Clock } from "lucide-react";
import { BRAND } from "@/config/brand";
import { api } from "@/lib/api";
import { trackUpgradeClick, trackCheckoutStart, trackAddToCart } from "@/lib/ga4";
import UpgradeOfferPricingCard from "@/components/pricing/UpgradeOfferPricingCard";
import { useAuth } from "@/context/AuthContext";

// Sprint V101 (12.02.2026) — Pricing-CTA-Guard.
// Verhindert, dass Nutzer den bereits aktiven Plan erneut anklicken/kaufen.
// Tier-Auflösung deckungsgleich zu PlanBadge.jsx (Single Source of Truth
// bleibt Backend-Feld user.license_tier / user.tier).
function resolveActiveTierKey(user) {
  if (!user || typeof user !== "object") return null;
  const raw = String(user.license_tier || user.tier || "").toLowerCase().trim();
  if (
    raw.startsWith("pro_solo_year") ||
    raw.startsWith("pro_solo_annual") ||
    raw.includes("yearly") ||
    raw.includes("annual")
  ) return "pro_solo_yearly";
  if (raw.startsWith("pro_solo_month") || raw.includes("monthly")) return "pro_solo_monthly";
  if (raw === "powerpaket" || user.powerpaket_lifetime === true) return "powerpaket";
  return "free";
}

// Sprint 13 AC #4 — Pricing-Seite Outcome statt Features.
// Vier Pläne, Outcome-getriebene Bullet-Liste, eine "Empfohlen"-Karte (Jahresplan).

// Sprint V11 (Boardroom-CoS Sa 05.09.2026-17 Uhr) — Preisseite auf 2 Tiles:
// Pro Monat 39 € + Pro Jahr 349 €. Free-Tile (Schnupper-Plan) entfernt — Free
// bleibt Standard-Zustand und wird ueber Signup/Charmeseite kommuniziert.
// PowerPaket-Standalone-Block, -Vorschau und -Standalone-Hint entfernt —
// PowerPaket erscheint nur noch als Bundle-Badge beim Pro-Jahr-Tile.
//
// Hotfix Pricing-Consolidation (10.02.2026) — Boardroom-CoS-Directive:
// CTAs zeigen NICHT mehr auf /dashboard/abo (alte Zwischenseite entfällt),
// sondern direkt auf die Ablefy-Checkout-URLs aus /api/pricing/pro-solo.
// Preise, Produktbedingungen und Checkout-Ziele bleiben unverändert —
// nur die Zwischenroute wird gespart.
const PLANS = [
  {
    id: "pro-monthly",
    testId: "pricing-card-pro-monthly",
    name: "Monatsplan",
    price: "39 €",
    period: "/ Monat",
    // Sprint V102.i (23.09.2026) — 7-Tage-Trial-Bridge.
    trialBadge: "7 Tage für 1 €",
    trialSub: "Ohne Risiko starten, Zugang sofort, regulärer Preis ab Tag 8.",
    sub: "Volle Tiefe. Monatlich kündbar.",
    bullets: [
      "Du verstehst jeden Fokustyp — nicht nur deinen — in der Tiefe.",
      "Du gehst auf andere zu, ohne dass sich jemand gedrängt fühlt.",
      "Du siehst deine eigenen Muster ab dem siebten Flexpfad.",
      "Du behältst alles: bearbeiten, löschen, wiederfinden.",
    ],
    cta: { label: "Für 1 € starten — 7 Tage testen", checkoutKey: "monthly" },
    highlighted: false,
  },
  {
    id: "pro-yearly",
    testId: "pricing-card-pro-yearly",
    name: "Jahresplan",
    price: "349 €",
    period: "/ Jahr",
    sub: "Wie Monatsplan — nur zeitliche Erweiterung. Wer dranbleibt, spart.",
    bullets: [
      "Alles aus dem Monatsplan — funktional identisch.",
      "12 Monate durchgehender Zugriff, ohne monatliche Abrechnung.",
      "2 Monate gespart gegenüber Monatsplan.",
      "PowerPaket inklusive — Vertiefungskurs als Lifetime-Zugriff.",
    ],
    cta: { label: "Jahresplan wählen", checkoutKey: "yearly" },
    highlighted: true,
    badge: "Empfohlen",
  },
];

function PriceCard({ plan, checkoutUrls, activeTierKey }) {
  const isHighlighted = plan.highlighted;
  // Sprint V11 (Boardroom Sa 05.09.2026-17 Uhr) — Bundle-Badge dauerhaft beim
  // Pro-Jahr-Tile, nicht mehr abhaengig von PowerPaket-Fenster.
  const showBundleBadge = plan.id === "pro-yearly";
  // Hotfix Pricing-Consolidation (10.02.2026) — Checkout-URL direkt aus
  // /api/pricing/pro-solo (identisch zur bisherigen DashboardAbo-Logik).
  // Solange die URL noch geladen wird, ist der CTA disabled statt broken.
  const checkoutHref = checkoutUrls?.[plan.cta.checkoutKey] || "";

  // Sprint V101 — Aktiv-Plan-Erkennung: Plan-ID mit Tier-Key mappen.
  const planTierKey =
    plan.id === "pro-monthly" ? "pro_solo_monthly" :
    plan.id === "pro-yearly" ? "pro_solo_yearly" : null;
  const isActivePlan = !!activeTierKey && planTierKey === activeTierKey;
  // Jahresplan-Kunden decken den Monatsplan bereits ab. PowerPaket-Lifetime
  // enthält den Jahresplan als Bundle — daher sind alle Pro-Tiles gesperrt.
  const isCoveredByHigherTier =
    (activeTierKey === "pro_solo_yearly" && plan.id === "pro-monthly") ||
    (activeTierKey === "powerpaket" && (plan.id === "pro-monthly" || plan.id === "pro-yearly"));

  const ctaDisabled = !checkoutHref || isActivePlan || isCoveredByHigherTier;
  const ctaLabel = isActivePlan
    ? "Aktueller Plan"
    : isCoveredByHigherTier
      ? "Bereits enthalten"
      : plan.cta.label;
  return (
    <div
      data-testid={plan.testId}
      className={`relative rounded-2xl p-7 sm:p-8 flex flex-col h-full min-h-[420px] ${
        isHighlighted
          ? "bg-slate-800 dark:bg-[var(--fts-primary-deep)] text-white shadow-xl border-2 border-[var(--fts-primary-bright)]"
          : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
      }`}
    >
      {isHighlighted && plan.badge && (
        <div
          data-testid={`${plan.testId}-badge`}
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[var(--fts-primary-bright)] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1"
        >
          <Sparkles className="w-3 h-3" />
          {plan.badge}
        </div>
      )}
      <h3 className={`text-lg font-extrabold tracking-tight ${isHighlighted ? "text-white" : "text-slate-900 dark:text-white"}`}>
        {plan.name}
      </h3>
      <div className="mt-4 flex items-baseline gap-1">
        <span className={`text-4xl font-extrabold tracking-tight ${isHighlighted ? "text-white" : "text-slate-900 dark:text-white"}`}>
          {plan.price}
        </span>
        <span className={`text-sm ${isHighlighted ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>{plan.period}</span>
      </div>
      {showBundleBadge && (
        <div
          data-testid={`${plan.testId}-bundle-badge`}
          className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[11px] font-bold uppercase tracking-wider self-start"
        >
          <Package className="w-3 h-3" /> + PowerPaket dabei
        </div>
      )}
      {plan.trialBadge && (
        <div
          data-testid={`${plan.testId}-trial-badge`}
          className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider self-start"
        >
          <Sparkles className="w-3 h-3" /> {plan.trialBadge}
        </div>
      )}
      <p className={`mt-2 text-sm ${isHighlighted ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>{plan.sub}</p>
      {plan.trialSub && (
        <p
          data-testid={`${plan.testId}-trial-sub`}
          className={`mt-1 text-xs font-semibold ${
            isHighlighted ? "text-emerald-300" : "text-emerald-700 dark:text-emerald-400"
          }`}
        >
          {plan.trialSub}
        </p>
      )}
      <ul className="mt-6 space-y-2.5 text-sm flex-1">
        {plan.bullets.map((b, i) => (
          <li
            key={i}
            data-testid={`${plan.testId}-bullet-${i}`}
            className={`flex gap-2 items-start ${isHighlighted ? "text-slate-100" : "text-slate-700 dark:text-slate-200"}`}
          >
            <Check
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                isHighlighted ? "text-[var(--fts-primary-bright)]" : "text-[var(--fts-success)]"
              }`}
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <a
        href={ctaDisabled ? "#" : (checkoutHref || "#")}
        onClick={(e) => {
          if (ctaDisabled) { e.preventDefault(); return; }
          // Sprint V27 GA4 — Trichter-Events: upgrade_click + begin_checkout
          const planId = plan.id || plan.testId;
          const priceMatch = String(plan.price || "").replace(/[^0-9,.]/g, "").replace(",", ".");
          const priceNum = parseFloat(priceMatch) || null;
          trackUpgradeClick({ location: "pricing_card", plan: planId });
          trackAddToCart({ plan: planId, value: priceNum, location: "pricing_card" });
          trackCheckoutStart({ plan: planId, value: priceNum });
        }}
        aria-disabled={ctaDisabled}
        data-testid={`${plan.testId}-cta`}
        data-cta-state={isActivePlan ? "active-plan" : isCoveredByHigherTier ? "covered" : "available"}
        className={`mt-auto inline-flex items-center justify-center gap-2 rounded-xl font-bold px-5 py-3 text-sm transition-opacity ${
          isHighlighted
            ? "bg-[var(--fts-primary-bright)] hover:opacity-90 text-white"
            : "bg-slate-900 dark:bg-[var(--fts-primary-deep)] hover:opacity-90 text-white"
        } ${ctaDisabled ? "opacity-50 pointer-events-none cursor-not-allowed" : ""}`}
      >
        {ctaLabel} {!ctaDisabled && <ArrowRight className="w-4 h-4" />}
      </a>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const activeTierKey = resolveActiveTierKey(user);
  const [ppStatus, setPpStatus] = useState(null);
  // Hotfix Pricing-Consolidation (10.02.2026) — Checkout-URLs zentral aus
  // /api/pricing/pro-solo (identisch zur bisherigen DashboardAbo-Quelle).
  // Keine neue Pricing-Logik, nur Übernahme der bestehenden Backend-Werte.
  const [checkoutUrls, setCheckoutUrls] = useState({ monthly: "", yearly: "" });

  useEffect(() => {
    api
      .get("/powerpaket/status")
      .then((r) => setPpStatus(r.data))
      .catch(() => setPpStatus(null));
    api
      .get("/pricing/pro-solo")
      .then((r) => {
        const urls = (r.data && r.data.checkout_urls) || {};
        setCheckoutUrls({
          monthly: urls.monthly || "",
          yearly: urls.yearly || "",
        });
      })
      .catch(() => setCheckoutUrls({ monthly: "", yearly: "" }));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-slate-950 flex flex-col" data-testid="pricing-page">
      {/* Sprint V102.i.1 (Boardroom-Directive) — Header-Konsistenz.
          Eingeloggte User sehen den Dashboard-Header (gleiches Logo, gleiche
          Nav wie im Cockpit), anonyme Besucher weiterhin den Marketing-Header.
          Verhindert den „fremden" Look beim Sprung Cockpit → Preise. */}
      {user && user !== false && user.email ? (
        <DashboardHeader
          firstName={user?.first_name}
          statusLabel={user?.account_status_label}
          statusColor={user?.account_status_color}
          licenseTier={user?.license_tier}
          licenseStatus={user?.license_status}
          licenseData={user}
        />
      ) : (
        <Header />
      )}
      {/* Sprint V102.j — Marketing-Banner-Slot (Trial-Push u.a.). Rendert
          nur fuer eingeloggte User mit passendem Tier. */}
      <div className="max-w-6xl mx-auto w-full px-6 lg:px-8 pt-4">
        <MarketingBanner />
      </div>
      <main className="flex-1 px-6 py-20 sm:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1E40AF] text-xs font-bold uppercase tracking-wider">
              <Tag className="w-3.5 h-3.5" />
              Preise
            </div>
            <h1
              className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white leading-tight"
              data-testid="pricing-headline"
            >
              Wähle, wie weit du gehen willst.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Zwei Pläne. Ein Ziel: Deine Botschaft kommt an. Bei jedem.
            </p>
          </div>

          {/* Sprint V30 (Fr 27.02.2026) — Retention Upgrade Deal-Karte.
              Rendert sich nur fuer Pro-Solo-Monatsabonnenten im 28-Tage-
              Fenster (self-rendering, null sonst). Prominent oben. */}
          <div className="mt-10 max-w-3xl mx-auto">
            <UpgradeOfferPricingCard />
          </div>

          {/* Sprint V11 (Boardroom Sa 05.09.2026-17 Uhr) — PowerPaket-Standalone
              nicht mehr auf Preisseite. Nur 2 Tiles (Monat + Jahr mit Bundle). */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch max-w-3xl mx-auto">
            {PLANS.map((p) => (
              <PriceCard key={p.id} plan={p} checkoutUrls={checkoutUrls} activeTierKey={activeTierKey} />
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-slate-400 dark:text-slate-500 max-w-2xl mx-auto">
            Alle Pläne mit jederzeitigem Wechsel. Monatsplan monatlich kündbar.
            Kein versteckter Aufpreis, keine Setup-Gebühr. {BRAND.name} ist DSGVO-konform.
          </p>
        </div>
      </main>
      <Footer />
      {/* Sprint V102.j — Campaign-Modal auch auf /pricing, damit Free-User
          die Trial-Message direkt beim ersten Preise-Aufruf sehen. */}
      <CampaignModal />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sprint M5.35 (29.07.2026) — PowerPaket-Bundle-Block mit Countdown.
// Nur sichtbar, wenn `/api/powerpaket/status.fenster_active === true`.
// ---------------------------------------------------------------------------
function PowerPaketBundle({ status }) {
  const [timeLeft, setTimeLeft] = useState(() => _computeTimeLeft(status?.end));

  useEffect(() => {
    if (!status?.end) return undefined;
    const t = setInterval(() => setTimeLeft(_computeTimeLeft(status.end)), 60_000);
    return () => clearInterval(t);
  }, [status?.end]);

  return (
    <section
      data-testid="pricing-powerpaket-bundle"
      className="mt-10 rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-6 sm:p-8"
    >
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow">
          <Package className="w-8 h-8" />
        </div>
        <div className="flex-1">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold uppercase tracking-wider"
            data-testid="pricing-powerpaket-badge"
          >
            <Sparkles className="w-3 h-3" />
            Launch-Angebot aktiv
          </div>
          <h2
            className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
            data-testid="pricing-powerpaket-headline"
          >
            PowerPaket — kostenfrei zum Jahresplan
          </h2>
          <p className="mt-2 text-sm sm:text-base text-slate-700 max-w-2xl leading-relaxed">
            Wer sich in diesem Launch-Fenster für den <strong>Jahresplan (349 €)</strong> entscheidet,
            bekommt das <strong>PowerPaket</strong> automatisch dazu — als Lifetime-Version, dauerhafter Zugriff.
          </p>

          <div
            className="mt-4 flex flex-wrap items-center gap-3 text-sm"
            data-testid="pricing-powerpaket-countdown"
          >
            <Clock className="w-4 h-4 text-amber-700" />
            <span className="font-bold text-amber-900">Noch {timeLeft}</span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-600">Launch-Fenster endet {_formatEnd(status?.end)}</span>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={status?.yearly_checkout_url || "/pricing"}
              data-testid="pricing-powerpaket-cta-year"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-3 text-sm shadow"
            >
              Jahresplan + PowerPaket sichern <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Sprint M5.39 (30.07.2026) — Block C1: PowerPaket-Vorschau (immer sichtbar).
// Fuer Free, Pro und Neugierige. PowerPaket ist eigenstaendig kaufbar.
// Bundle-Promotion (dazu bei Jahresplan) sichtbar NUR im Fenster.
function PowerPaketVorschau({ status }) {
  const checkoutUrl = process.env.REACT_APP_ABLEFY_CHECKOUT_URL_POWER_PAKET
    || status?.checkout_url
    || `https://ablefy.de/product/${status?.product_id || 524805}`;
  const fensterActive = Boolean(status?.fenster_active);
  return (
    <section
      data-testid="pricing-powerpaket-vorschau"
      className="mt-14 rounded-3xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-slate-900 p-6 sm:p-8 max-w-5xl mx-auto"
    >
      <div className="flex flex-col md:flex-row items-start gap-6">
        <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow">
          <Package className="w-7 h-7" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">
            PowerPaket · Lifetime-Version · Side-Tack
          </p>
          <h2
            className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white"
            data-testid="pricing-powerpaket-vorschau-headline"
          >
            PowerPaket — Vertiefungskurs für bewusste Kommunikation auf Wellenlänge
          </h2>
          <p
            className="mt-3 text-sm sm:text-base text-slate-700 dark:text-slate-200 max-w-2xl leading-relaxed"
            data-testid="pricing-powerpaket-vorschau-desc"
          >
            Lerne, wie du Türen öffnest, ohne Widerstände und Missverständnisse. Vertiefung
            zu allen vier Fokustypen — Erwartungen, Bedürfnisse, Kommunikationsstile.
            Eigenständiger Add-on-Kurs, auch ohne Pro Solo kaufbar. Lifetime-Zugriff.
          </p>

          <div
            className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
            data-testid="pricing-powerpaket-vorschau-preise"
          >
            <span className="inline-flex items-center gap-2">
              <span className="text-2xl font-extrabold text-slate-900 dark:text-white">197 €</span>
              <span className="text-slate-500 dark:text-slate-400">einmalig</span>
            </span>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <span className="inline-flex items-center gap-2">
              <span className="text-lg font-bold text-slate-700 dark:text-slate-200">217 €</span>
              <span className="text-slate-500 dark:text-slate-400">über 3 Raten (3 × 72,50 €)</span>
            </span>
          </div>

          {fensterActive && (
            <div
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 px-3 py-2"
              data-testid="pricing-powerpaket-vorschau-bundle-note"
            >
              <Sparkles className="w-4 h-4 text-amber-700 dark:text-amber-400" />
              <span className="text-xs sm:text-sm font-semibold text-amber-900 dark:text-amber-200">
                Launch-Angebot: Mit Pro Solo Jahr bis 13.08.2026 kostenfrei dazu.
              </span>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-powerpaket-vorschau-cta"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-3 text-sm shadow"
            >
              PowerPaket ansehen <ArrowRight className="w-4 h-4" />
            </a>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              Auch einzeln kaufbar — 197 € einmalig oder 217 € über 3 Raten.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}


function PowerPaketStandaloneHint({ status }) {
  return (
    <p
      className="mt-8 text-center text-sm text-slate-600 dark:text-slate-300 max-w-2xl mx-auto"
      data-testid="pricing-powerpaket-standalone-hint"
    >
      Auch einzeln als <strong>PowerPaket · Lifetime-Version</strong> für {status?.price || 147} € erhältlich —
      Add-on für alle Plans (auch Free). <br className="hidden sm:inline" />
      <a
        href={status?.checkout_url || `https://ablefy.de/product/${status?.product_id || 524805}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#4563BB] font-semibold hover:underline"
        data-testid="pricing-powerpaket-standalone-link"
      >
        Zum Ablefy-Angebot →
      </a>
    </p>
  );
}

function _computeTimeLeft(endIso) {
  if (!endIso) return "—";
  const end = new Date(endIso);
  const diff = end - new Date();
  if (diff <= 0) return "0 Std";
  const days = Math.floor(diff / (24 * 3600 * 1000));
  const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
  if (days > 0) return `${days} Tag${days === 1 ? "" : "e"} ${hours} Std`;
  return `${hours} Std`;
}

function _formatEnd(endIso) {
  if (!endIso) return "";
  try {
    const d = new Date(endIso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch (_) {
    return "";
  }
}
